import { type FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../shared/auth/useAuth';
import { displayName } from '../shared/branding';
import { Button } from '../shared/components/Button';
import { Input } from '../shared/components/Input';
import { LumeMark } from '../shared/components/LumeMark';
import { useTranslation } from '../shared/i18n/useTranslation';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = (location.state as LocationState | null)?.from ?? '/app';

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(fromPath, { replace: true });
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      const isNetwork = code.toLowerCase().includes('network');
      setError(isNetwork ? t('auth.login.error.network') : t('auth.login.error.invalid'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 p-6">
      <header className="flex flex-col items-center gap-3 text-center">
        <LumeMark size={64} title={displayName('user-facing')} />
        <p className="text-3xl font-bold lowercase tracking-tight text-brand-900">
          {displayName('user-facing')}
        </p>
        <h1 className="text-2xl font-semibold text-fg-DEFAULT">{t('auth.login.title')}</h1>
      </header>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label={t('auth.login.email')}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label={t('auth.login.password')}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error ? (
          <p role="alert" className="text-sm text-danger-700">
            {error}
          </p>
        ) : null}
        <Button type="submit" disabled={submitting} aria-busy={submitting} size="n2">
          {submitting ? t('common.loading') : t('auth.login.submit')}
        </Button>
      </form>
      <nav className="flex flex-col gap-2 text-sm">
        <Link to="/signup" className="text-brand-800 underline">
          {t('auth.login.signup')}
        </Link>
      </nav>
    </main>
  );
}
